import { useEffect, useState } from 'react'

/**
 * Default window we'll spin before declaring Logto's session rehydrate
 * "stuck". Chosen to be longer than any normal token-refresh round-trip
 * against staging/prod Logto + Cloudflare (P99 ~2s) while still being well
 * under a user's patience for "did the page break?".
 */
export const DEFAULT_AUTH_LOADING_TIMEOUT_MS = 15_000

/**
 * Watchdog for the Logto auth `isLoading` state. The Logto JS SDK gates
 * `isLoading` on its internal in-flight-call counter, which can stay
 * non-zero forever if a network call (refresh_token grant, /oidc/me) hangs
 * without timing out — the classic tab-was-asleep-for-a-week revisit
 * symptom that ships a permanent spinner. There is no fetch timeout inside
 * the SDK, so the only safe boundary is at the React boundary.
 *
 * Pass `active=true` while you're showing a Logto-gated spinner and
 * `active=false` the moment you transition to authenticated or signed-out
 * UI. When `active` stays true for `timeoutMs`, the hook flips its return
 * value to `true` so the caller can render a recovery UX (re-sign-in
 * button, hard reload) instead of spinning indefinitely. The flag resets
 * to `false` the instant `active` flips false, so a slow-but-eventually-
 * resolving rehydrate doesn't leave the warning UI stuck on screen.
 */
export function useAuthLoadingWatchdog(
  active: boolean,
  timeoutMs: number = DEFAULT_AUTH_LOADING_TIMEOUT_MS,
): boolean {
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    if (!active) {
      // Intentional synchronous reset so a slow-but-eventually-resolving
      // rehydrate immediately clears the warning UI on the same commit
      // instead of leaving the recovery card up for one extra render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStuck(false)
      return
    }
    const timer = setTimeout(() => setStuck(true), timeoutMs)
    return () => clearTimeout(timer)
  }, [active, timeoutMs])

  return stuck
}
